import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { vacancyAPI } from '../services/api';
import toast from 'react-hot-toast';

export interface Vacancy {
  id: string;
  employer_id: string;
  search_request_id?: number;
  titel: string;
  beschrijving: string;
  professional_type: string;
  gender_preference: 'male' | 'female' | 'no_preference';
  availability_needed: 'immediate' | 'specific_date';
  availability_date?: string;
  hours_per_month_min?: number;
  hours_per_month_max?: number;
  hours_per_month_staffel?: string;
  required_languages: string[];
  active_regions: string[];
  work_preference: 'physical' | 'remote' | 'hybrid' | 'remote_only' | 'hybrid_50_50' | 'face_to_face' | 'no_preference';
  years_experience_min?: number;
  hourly_rate_max?: number;
  industry_experience_preferred: string[];
  status: 'draft' | 'published' | 'closed' | 'filled';
  published_at?: string;
  closed_at?: string;
  view_count: number;
  application_count: number;
  created_at: string;
  updated_at: string;
  employer_company_name?: string;
  employer_logo?: string;
  company_description?: string;
}

export interface VacancyListResponse {
  data: Vacancy[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export const useVacancies = (params?: { 
  page?: number; 
  limit?: number; 
  status?: string; 
  search?: string;
  professional_type?: string;
  work_preference?: string;
  active_regions?: string | string[];
}) => {
  return useQuery<VacancyListResponse>({
    queryKey: ['vacancies', params],
    queryFn: async () => {
      const res = await vacancyAPI.getAll(params);
      // Handle response shape - ApiResponse wraps the data
      const responseData = res.data as any;
      
      // Backend returns: { success: true, data: [...], pagination: {...} }
      if (responseData.success && responseData.data && responseData.pagination) {
        return {
          data: Array.isArray(responseData.data) ? responseData.data : [],
          pagination: responseData.pagination
        };
      }
      
      // Fallback for different response structures
      if (responseData.data && responseData.pagination) {
        return {
          data: Array.isArray(responseData.data) ? responseData.data : [],
          pagination: responseData.pagination
        };
      }
      
      // Last fallback
      return {
        data: [],
        pagination: undefined
      };
    },
  });
};

export const useVacancy = (id: string) => {
  return useQuery({
    queryKey: ['vacancy', id],
    queryFn: () => vacancyAPI.getById(id).then(res => res.data.data),
    enabled: !!id,
  });
};

export const useApplyVacancy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, motivation }: { id: string; motivation?: string }) =>
      vacancyAPI.apply(id, motivation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacancies'] });
      queryClient.invalidateQueries({ queryKey: ['vacancy'] });
      queryClient.invalidateQueries({ queryKey: ['opdrachten'] });
      queryClient.invalidateQueries({ queryKey: ['opdracht'] });
      toast.success('Opdracht geaccepteerd!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Fout bij accepteren van opdracht');
    },
  });
};

// Employer: list applications for a vacancy
export const useVacancyApplications = (vacancyId: string | undefined) => {
  return useQuery({
    queryKey: ['vacancy-applications', vacancyId],
    queryFn: async () => {
      const res = await api.get(`/employer/vacancies/${vacancyId}/applications`);
      return (res.data?.data || []) as VacancyApplication[];
    },
    enabled: !!vacancyId,
  });
};

export interface VacancyApplication {
  id: number;
  vacancy_id: string;
  professional_id: string;
  /** Anoniem tot acceptatie: naam, e-mail, foto, bedrijfsnaam en CV zijn dan null */
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  anoniem?: boolean;
  kandidaat_nummer?: number;
  compliance_tier?: 'GOUD' | 'ZILVER' | 'BRONS' | 'ONGEVERIFIEERD' | 'NON_COMPLIANT_HOLD';
  compliance_indicatoren?: { kvk: boolean; polis: boolean; arbo: boolean; register: boolean } | null;
  status: string;
  motivation?: string;
  company_name?: string;
  certifications?: string[];
  available_from?: string;
  hours_per_week?: number;
  rate_proposal?: number;
  profile_hourly_rate?: number;
  relevant_experience?: string;
  confidentiality_declared: boolean;
  cv_attachment_url?: string;
  employer_response?: string;
  uitnodiging_type?: string;
  employer_response_message?: string;
  employer_responded_at?: string;
  profile_photo_url?: string;
  applied_at: string;
}

// Employer: respond to an application
export const useRespondToApplication = (vacancyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ appId, action, uitnodiging_type, message }: {
      appId: number;
      action: 'accepteer' | 'uitnodiging' | 'afwijzen';
      uitnodiging_type?: string;
      message?: string;
    }) => api.post(`/employer/vacancies/${vacancyId}/applications/${appId}/respond`, { action, uitnodiging_type, message }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacancy-applications', vacancyId] });
      toast.success('Reactie verstuurd');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Fout bij versturen reactie');
    },
  });
};

